// Auth "writers": login, redirect handling, logout, and writing the app-user doc.

import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
} from "firebase/auth";
import { setDoc, getDoc, doc, serverTimestamp } from "firebase/firestore";

import { getFirebase, waitAuthPersistence } from "../../core/firebaseApp";
import { COLLECTIONS } from "../../core/collections";

/** Mobile-first detection for choosing redirect vs popup */
function isLikelyMobile() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const touch = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(pointer:coarse)").matches
    : false;
  return /Android|iPhone|iPad|iPod/i.test(ua) || touch;
}

export async function loginWithGoogle() {
  const { auth, ready, reason } = getFirebase();
  if (!ready) throw new Error(`Firebase not configured: ${reason}`);
  await waitAuthPersistence();

  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  // Mobile: redirect is most reliable
  if (isLikelyMobile()) {
    await signInWithRedirect(auth, provider);
    return { method: "redirect" };
  }

  // Desktop: try popup; fall back to redirect
  try {
    const cred = await signInWithPopup(auth, provider);
    await writeAppUser(cred.user);
    return { method: "popup", user: cred.user };
  } catch (err) {
    if (err?.code === "auth/popup-blocked" || err?.code === "auth/popup-closed-by-user") {
      await signInWithRedirect(auth, provider);
      return { method: "redirect" };
    }
    throw err;
  }
}

export async function handleAuthRedirect() {
  const { auth, ready } = getFirebase();
  if (!ready) return null;
  await waitAuthPersistence();

  try {
    const res = await getRedirectResult(auth);
    if (res?.user) {
      await writeAppUser(res.user);
      return res.user;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("getRedirectResult warning:", e?.code || e?.message);
  }
  return null;
}

export async function logoutUser() {
  const { auth, ready } = getFirebase();
  if (ready) await signOut(auth);
}

// uid-keyed app profile; do not clobber admin on subsequent writes
export async function writeAppUser(user) {
  const { db, ready } = getFirebase();
  if (!ready || !user) return;

  const docId = String(user.uid || "").trim();
  const colName = (COLLECTIONS && COLLECTIONS.systemAppUsers) || "app_users";
  const ref = doc(db, colName, docId);

  let existing = null;
  try {
    const snap = await getDoc(ref);
    existing = snap.exists() ? snap.data() : null;
  } catch { existing = null; }
  const isFirstWrite = !existing;

  const now = serverTimestamp();
  const base = {
    uid: user.uid ?? null,
    email: user.email ?? null,
    displayName: user.displayName ?? null,
    photoURL: user.photoURL ?? null,
    providerId: user.providerData?.[0]?.providerId ?? "google.com",
    lastLoginAt: now,
  };

  await setDoc(
    ref,
    { ...(isFirstWrite ? { signUpAt: now, createdAt: now, admin: false } : {}), ...base },
    { merge: true }
  );
}

// keep the alias used by the app
export const fcLoginWithGoogle = loginWithGoogle;
